import { useMemo } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'

import {
  FBPageInnerTabsContainer,
  FBPageTableContainer,
} from 'components/FbUI/Page/styles'
import { UnderlineTabs } from 'components/FbUI/UnderlineTabs'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { EmailIntegrationTable } from 'features/settings/components/EmailIntegrationTable'
import { EmailIntegrationSignatures } from 'features/settings/components/EmailIntegrationSignatures'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

type EmailIntegrationTab = 'connections' | 'signatures'

export function EmailIntegrationSettingsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const featureFlags = useFeatureFlagService()
  const { preferences } = usePreferences()
  const signaturesTooltip = preferences?.tooltips?.settings_email_signatures

  const tabs = useMemo(() => {
    const allTabs: { key: EmailIntegrationTab; title: string }[] = [
      { key: 'connections', title: 'Connections' },
    ]

    // Signatures power the Action Center send-email apply path, which
    // is itself gated on the homepage flag — keep them in lockstep so
    // accounts that don't have the homepage UX don't see an orphaned
    // signatures tab.
    if (featureFlags.enableHomepage) {
      allTabs.push({ key: 'signatures', title: 'Email Signatures' })
    }

    return allTabs
  }, [featureFlags.enableHomepage])

  const defaultTab: EmailIntegrationTab = tabs[0]?.key ?? 'connections'
  const tab = (params.tab as EmailIntegrationTab) ?? defaultTab

  if (!featureFlags.enableEmailIntegration) {
    return <Navigate to="/settings/team/users" replace />
  }

  if (params.tab && !tabs.some((t) => t.key === params.tab)) {
    return <Navigate to={`/settings/email-integration/${defaultTab}`} replace />
  }

  return (
    <div className="w-full h-full flex flex-col flex-1 min-h-0 bg-[#fefefe]">
      <FBPageInnerTabsContainer>
        <UnderlineTabs<EmailIntegrationTab>
          tabs={tabs}
          activeKey={tab}
          onChange={(value) => navigate(`/settings/email-integration/${value}`)}
          idPrefix="email-integration-settings"
          aria-label="Email integration settings tabs"
        />
      </FBPageInnerTabsContainer>
      <FBPageTableContainer
        className="bg-[#fefefe]"
        hide={tab !== 'connections'}
      >
        <EmailIntegrationTable />
      </FBPageTableContainer>
      {featureFlags.enableHomepage && (
        <FBPageTableContainer
          className="bg-[#fefefe]"
          hide={tab !== 'signatures'}
        >
          <div className="flex items-center gap-1 px-1 pt-1">
            <span className="text-base font-semibold text-fg">
              Email Signatures
            </span>
            {signaturesTooltip && (
              <HelpTooltip
                title={signaturesTooltip.title}
                content={signaturesTooltip.content}
              />
            )}
          </div>
          <EmailIntegrationSignatures />
        </FBPageTableContainer>
      )}
    </div>
  )
}
